import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import { Grid, Checkbox, Radio, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/styles';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import { keyEmptyCheck } from 'aurora-utils/utils';
import ADGMItemDetails from '../ADGMItemDetails/ADGMItemDetails';
import ADGMImage from '../ADGMImage';
import styles from './styles';

/**
 * * This component can be used as the Content Panel of the Tabs or you can use your custom component.
 * @param {gridData} - array of labels & values, add "isPersonName" in name data
 * @param {withIcon} - shows the icon
 * @param {defaultValue} - default value if value not found
 * @param {hasPadding} - flag to add default padding
 * @param {customClasses} - custom classes to override styles
 * @param {gridProps} - grid props
 * @returns { node }
 */

const IS_PERSON_NAME = 'isPersonName';

const ADGMGridLayoutV2 = ({
  classes,
  gridData,
  gridColumns,
  nameLabel,
  withIcon,
  iconName,
  defaultValue,
  noPadding,
  selectableProps,
  customClasses,
  ...gridProps
}) => {
  if (!isObject(gridData) || isEmpty(gridData)) return null;

  const theme = useTheme();

  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    selectable = false,
    selected = false,
    multi = false,
    disabled = false,
    setSelected = () => {},
    ...otherProps
  } = selectableProps;

  const nameData = useMemo(() => {
    return gridData?.find(item => item?.[IS_PERSON_NAME]) || {};
  }, [gridData]);

  const data = useMemo(() => {
    return isMobileView ? gridData?.filter(item => !item?.[IS_PERSON_NAME]) : gridData;
  }, [isMobileView, gridData]);

  const renderGridItem = itemData => {
    if (isEmpty(itemData)) return null;
    return (
      <Grid
        key={itemData?.label}
        item
        md={gridColumns}
        sm={12}
        xs={12}
        {...gridProps}
        className={classNames(classes.gridItem, customClasses?.gridItem)}
      >
        <ADGMItemDetails label={keyEmptyCheck(itemData?.label)} value={itemData?.value || defaultValue} />
      </Grid>
    );
  };

  return (
    <div
      className={classNames(classes.container, customClasses?.container, {
        [classes.noContainerPadding]: noPadding,
      })}
    >
      {withIcon && (
        <div className={classNames(classes.iconAndNameHolder, customClasses.iconAndNameHolder)}>
          {selectable &&
            (multi ? (
              <Checkbox
                edge='start'
                checked={selected}
                onChange={e => setSelected(e.target.checked)}
                disableRipple
                disabled={disabled}
                classes={{
                  root: classNames(classes.selectedHolder, customClasses?.selectedHolder),
                  checked: classNames(classes.selected, customClasses.selected, {
                    [classes.selectedDisable]: disabled,
                  }),
                }}
                {...otherProps}
              />
            ) : (
              <Radio
                edge='start'
                checked={selected}
                disableRipple
                disabled={disabled}
                classes={{
                  root: classNames(classes.selectedHolder, customClasses?.selectedHolder),
                  checked: classNames(classes.selected, customClasses.selected, {
                    [classes.selectedDisable]: disabled,
                  }),
                }}
                {...otherProps}
              />
            ))}
          <ADGMImage
            name={iconName || 'user_profileV3.svg'}
            alt='icon'
            className={classNames(classes.icon, customClasses.icon)}
          />
          {isMobileView && (
            <div className={classNames(classes.nameHolder, customClasses.nameHolder)}>
              <ADGMItemDetails
                label={keyEmptyCheck(nameLabel || nameData?.label)}
                value={nameData?.value || defaultValue}
              />
            </div>
          )}
        </div>
      )}
      <Grid
        className={classNames(classes.gridItemsHolder, customClasses.gridItemsHolder)}
        item
        container
        md={12}
        spacing={2}
      >
        {data?.map(item => renderGridItem(item))}
      </Grid>
    </div>
  );
};

ADGMGridLayoutV2.propTypes = {
  classes: PropTypes.object.isRequired,
  gridData: PropTypes.array.isRequired,
  selectableProps: PropTypes.object,
  nameLabel: PropTypes.any,
  noPadding: PropTypes.bool,
  defaultValue: PropTypes.any,
  customClasses: PropTypes.object,
  withIcon: PropTypes.bool,
  gridColumns: PropTypes.number,
  iconName: PropTypes.string,
};

ADGMGridLayoutV2.defaultProps = {
  selectableProps: {},
  noPadding: false,
  defaultValue: '-',
  withIcon: false,
  iconName: '',
  customClasses: {},
  nameLabel: '',
  gridColumns: 4,
};

export default withStyles(styles)(ADGMGridLayoutV2);
